import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    Link,
    List,
    ListItem,
    MenuItem,
    Paper,
    Popper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import * as Yup from 'yup';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { RootState } from '../../../reducers';

import {
    fetchCptCodeGroups,
    fetchDiagnosisCodesFromClaims,
    getCPTUnits,
    getDefaultDiagnosis,
    getEncounterFromSummaryNotes,
    getICD10DiagnosisByCategory,
    getICD10DiagnosisByKeyword,
    getPastDiagnosisByMember,
    insertEncounters,
    insertEncountersNotAsssessment,
    resetDisgnosysList,
    setDefaultDiagnosis,
    setEncounterList,
    updateDefaultDiagnosis,
    prepareICD10Hierarchy,
} from '../../../store/encounter.slice';
import { updateAppointmentIdListToStatus, resetDocumentEngagement, fetchMemberAssessmentHistory } from '../../../store/documentEngagement.slice';
import { assessmentMenuEnum, assessmentStatusEnum, roleEnum } from '../../../utils/assessments';
import Authorization from '../../../utils/Authorization';
import { CPTcode } from '../../../Models/Shared/CPTCodeGroup';
import { copyObject, getCPTUserRole } from '../../../utils/common';
import { MemberEncounter } from '../../../Models/Member/MemberEncounter.model';
import { DiagnosisDraft, DiagnosisPointer } from '../../../Models/DocumentEngagements/DiagnosisDraft.model';
import { ICD10 } from '../../../Models/Shared/ICD10.model';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { EncounterStatusEnum } from '../../../Enum/EncounterStatusEnum';
import { MeansOfEngagementEnum } from '../../../Enum/MeansOfEngagementEnum';
import { getMemberAllergies, setAssessmentError, resetAssessment, addCompletedAssessmentTimelines } from '../../../store/assessment.slice';
import classes from '../Styles';
import { EncounterList } from '../../../Models/Encounters/EncounterList';
import { DiagnosisTypeEnum } from '../../../Enum/DiagnosisTypeEnum';
import { AppointmentStatusEnum } from '../../../Enum/AppointmentStatusEnum';
import { fetchTransitionalCareById } from '../../../store/transitionalCare.slice';
import { getDiagnosisByMember } from '../../../store/clinicalData.slice';
import { fetchTobaccoAndDrugAssessmentByMemberId, resetHraAssessment } from '../../../store/hra.slice';
import { resetFuhFumAssessment } from '../../../store/fuhfum.slice';
import { MemberStatusEnum } from '../../../Enum/MemberStatusEnum';
import { fetchGenders, setShowMessage } from '../../../store/shared.slice';
import { saveGender, getMemberBasicInfo, setMemberBasicInfo } from '../../../store/memberDetail.slice';
import { hasDuplicateDiagnosisCodes } from '../../../store/procedureSignOffEncounter.slice';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import { useNavigate } from 'react-router-dom';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { fetchPlacesOfEncounter } from '../../../store/adminCPTCodesSlice.slice';
import { DegreeEnum } from '../../../Enum/DegreeEnum';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Procedure_Sign_Off.pdf';

const PopperMy = function (props) {
    return <Popper {...props} style={{ maxWidth: 'fit-content' }} placement="bottom-start" />;
};

class DiagnosisFromClaimsState {
    Show: boolean;
    Index?: number;
    DxIndex?: number;
    ParentPropName: string;
    PropName: string;

    constructor() {
        this.Show = false;
        this.Index = null;
    }
}

interface PendingSubmissionAction {
    isReview: boolean;
    buttonId: string;
}

export interface IProcedureSignOffProps {
    memberId: string;
    requestId?: string;
    disableWarnings?: boolean;
    noAppointments?: boolean;
    notAssessment?: boolean;
    isNotEncounterableOnly?: boolean;
    finalizeSignOff?: (notes: string) => void;
}

const defaultDiagnosisSchema = Yup.object().shape({
    Diagnosis: Yup.string().nullable().required('Primary diagnosis is required.'),
    FirstDiagnosis: Yup.object()
        .nullable()
        .when('DiagnosisFirstList', {
            is: (list: ICD10[]) => Boolean(list?.length),
            then: Yup.object()
                .nullable()
                .required('Diagnosis is required.')
                .shape({
                    Id: Yup.string().nullable().required('Diagnosis is required.'),
                }),
        }),
    SecondDiagnosis: Yup.object()
        .nullable()
        .when('DiagnosisSecondList', {
            is: (list: ICD10[]) => Boolean(list?.length),
            then: Yup.object()
                .nullable()
                .required('Diagnosis is required.')
                .shape({
                    Id: Yup.string().nullable().required('Diagnosis is required.'),
                }),
        }),
    ThirdDiagnosis: Yup.object()
        .nullable()
        .when('DiagnosisThirdList', {
            is: (list: ICD10[]) => Boolean(list?.length),
            then: Yup.object()
                .nullable()
                .required('Diagnosis is required.')
                .shape({
                    Id: Yup.string().nullable().required('Diagnosis is required.'),
                }),
        }),
    FourthDiagnosis: Yup.object()
        .nullable()
        .when('DiagnosisFourthList', {
            is: (list: ICD10[]) => Boolean(list?.length),
            then: Yup.object()
                .nullable()
                .required('Diagnosis is required.')
                .shape({
                    Id: Yup.string().nullable().required('Diagnosis is required.'),
                }),
        }),
});

const Home = 'Home';
const Phone = 'Phone';
const Telemedicine = 'Telemedicine';
const OfficeVisit = 'Office Visit';

const ProcedureSignOff: React.FC<IProcedureSignOffProps> = ({
    memberId,
    disableWarnings,
    noAppointments,
    notAssessment,
    isNotEncounterableOnly,
    finalizeSignOff,
}) => {
    const dispatch = useAppDispatch();
    const [focusInputName, setFocusInputName] = useState('');
    const [clickedBtnId, setClickedBtnId] = useState('');
    const [showCptErrorMsg, setShowCptErrorMsg] = useState(false);
    const [showDefaultDiagnosisModal, setShowDefaultDiagnosisModal] = useState<boolean>(false);
    const [showWarnings, setShowWarnings] = useState(true);
    const [showDxError, setShowDxError] = useState(false);
    const [assessmentWarnings, setAssessmentWarnings] = useState([]);
    const [diagnosisFromClaims, setDiagnosisFromClaims] = useState<DiagnosisFromClaimsState>(new DiagnosisFromClaimsState());
    const {
        cptCodeCategories,
        cptCodeGroups,
        cptUnits,
        encounterList,
        diagnosisFromClaims: claimsDiagnosis,
        memberPastDiagnosys,
        memberDefaultDiagnosis,
        isFetchingDisgnosys,
        diagnosys,
        isFetching,
        isSaving,
        isSavingDefaultDiagnosis,
    } = useSelector((state: RootState) => state.encounter);
    const { diagnosises, isDiagnosisFetching } = useSelector((state: RootState) => state.clinicalData);
    const { genders, isLoading, currentProvider } = useSelector((state: RootState) => state.shared);
    const { selectedRole, memberAllergies, assessmentMenuList } = useSelector((state: RootState) => state.assessment);
    const { engagementDraft, memberAssessmentHistory } = useSelector((state: RootState) => state.documentEngagement);
    const { memberTransitionalCare } = useSelector((state: RootState) => state.transitionalCare);
    const { memberBasicInfo, member } = useSelector((state: RootState) => state.memberDetail);
    const { tobaccoAndDrug } = useSelector((state: RootState) => state.hra);
    const { placesOfEncounters } = useSelector((state: RootState) => state.adminCPTCodesSlice);
    const diagnosisFromClaimsId = 'DiagnosisFromClaims';
    const authData = new Authorization();
    const isInHra = selectedRole === roleEnum.HRA;
    const isHraMember = memberBasicInfo?.IsInHra;
    const isNursePractitioner = authData.Role === roleEnum.NURSE_PRACTITIONER;
    const isCoder = authData.Role === roleEnum.CODER;
    const isInTransitionalCare = Boolean(memberBasicInfo?.IsInTransition || engagementDraft?.IsInTransition || engagementDraft?.TransitionalCareId);
    const diagnosisesByFilter = diagnosises.map((d) => {
        return { Id: d.Id, Code: d.Code, Name: d.Name, ICDcodeVersion: d.ICDcodeVersion } as ICD10;
    });
    const memberPastDiagnosysByFilter = memberPastDiagnosys.filter((d) => !(diagnosisesByFilter || []).some((cd) => cd.Code === d.Code));
    const claimsDiagnosisByFilter = claimsDiagnosis.filter(
        (d) => ![...(diagnosisesByFilter || []), ...memberPastDiagnosysByFilter].some((cd) => cd.Code === d.Code)
    );
    const [showDiagnosisAlert, setShowDiagnosisAlert] = useState(memberBasicInfo?.Diagnosis?.some((m) => m.Code === 'E10' || m.Code === 'E11'));
    const isNotEncounterable = engagementDraft.SummaryNotes.some((note) => note.IsNotEncounterable);
    const [eventValue, setEventValue] = useState();
    const [isReviewValue, setIsReviewValue] = useState(false);
    const [showHedisAlert, setShowHedisAlert] = useState(false);
    const [hedisAlertMessage, setHedisAlertMessage] = useState([]);
    const [disableSubmitToClaim, setDisableSubmitToClaim] = useState(false);
    const [showSubmissionSummaryModal, setShowSubmissionSummaryModal] = useState(false);
    const [pendingSubmissionAction, setPendingSubmissionAction] = useState<PendingSubmissionAction | null>(null);
    const navigate = useNavigate();
    useEffect(() => {
        dispatch(fetchGenders());
        dispatch(getMemberBasicInfo(memberId));
        dispatch(getEncounterFromSummaryNotes(memberId, engagementDraft?.Id));
        dispatch(getDefaultDiagnosis(memberId));
        dispatch(fetchCptCodeGroups(isInHra));
        dispatch(getMemberAllergies(memberId));
        if (Boolean(engagementDraft?.TransitionalCareId)) {
            dispatch(fetchTransitionalCareById(engagementDraft.TransitionalCareId));
        }
        dispatch(getCPTUnits());
        dispatch(fetchDiagnosisCodesFromClaims(memberId, engagementDraft?.AssessedRole?.Id));
        dispatch(getDiagnosisByMember(memberId, engagementDraft?.AssessedRole?.Id));
        dispatch(resetDisgnosysList());
        dispatch(getPastDiagnosisByMember(memberId, engagementDraft?.AssessedRole?.Id));
        if (!Boolean(engagementDraft?.TobaccoAndDrugAssessmentId)) {
            dispatch(fetchTobaccoAndDrugAssessmentByMemberId(memberId));
        }
        dispatch(fetchPlacesOfEncounter());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (Boolean(engagementDraft?.Id) && Boolean(memberId)) {
            dispatch(fetchMemberAssessmentHistory(memberId, engagementDraft?.Id, engagementDraft?.AssessedRole?.RoleName));
        }
    }, [engagementDraft?.Id, engagementDraft?.AssessedRole?.RoleName, memberId]);

    useEffect(() => {
        prepareBPDetail(encounterList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [encounterList]);

    useEffect(() => {
        if (!disableWarnings) {
            prepareWarnings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberTransitionalCare, memberAllergies, memberAssessmentHistory, assessmentMenuList]);

    const prepareCPTCodes = (categoryGroupId: string) => {
        const cptUserRole = getCPTUserRole(selectedRole);
        const normalizedRoles = [cptUserRole, selectedRole].filter(Boolean).map((role) => role.replace(/\s+/g, '').toLowerCase());
        const allowHraCodes =
            isHraMember &&
            ((isInHra && (authData.Role === roleEnum.NURSE_PRACTITIONER || authData.Role === roleEnum.BEHAVIOURAL_HEALTH_SPECIALIST)) ||
                selectedRole === roleEnum.NURSE_PRACTITIONER ||
                selectedRole === roleEnum.BEHAVIOURAL_HEALTH_SPECIALIST);
        const cptCodes = (cptCodeCategories.find((c) => c.Id === categoryGroupId)?.CPTRoleCodes || [])
            .filter((roleCode) => normalizedRoles.includes(roleCode.Role.replace(/\s+/g, '').toLowerCase()) || (allowHraCodes && roleCode.Role === 'HRA'))
            .flatMap((roleCode) => roleCode.Codes || [])
            .filter((code, index, codes) => codes.findIndex((candidate) => candidate.Id === code.Id) === index)
            .map((item) => ({ ...item, Name: `${item.Code} - ${item.Description}` }));

        return cptCodes as CPTcode[];
    };

    const filteredCPTCodes = (categoryGroupId: string) => {
        let cptCodes = prepareCPTCodes(categoryGroupId);
        cptCodes = cptCodes.filter((c) => c.Code !== '83036');
        return cptCodes as CPTcode[];
    };

    const prepareCPTUnits = () => {
        return cptUnits.filter((c) => c.Code === 'T1016');
    };

    const handleOnChangeCpt = (val: any, propName: string, index: number) => {
        let data = copyObject(encounterList);
        const modifierFields = ['Modifier', 'Modifier2', 'Modifier3', 'Modifier4'];

        if (modifierFields.includes(propName) && val) {
            // Prevent duplicate modifiers across all slots
            const otherModifiers = modifierFields.filter((f) => f !== propName).map((f) => data.Encounters[index][f] || '');
            if (otherModifiers.includes(val)) return;
        }

        data.Encounters[index][propName] = val;

        // When a modifier is cleared, compact remaining modifiers to fill the gap
        if (modifierFields.includes(propName) && !val) {
            const modifiers = modifierFields.map((f) => data.Encounters[index][f] || '');
            const compacted = modifiers.filter(Boolean);
            modifierFields.forEach((f, i) => {
                data.Encounters[index][f] = compacted[i] || '';
            });
        }

        if (propName === 'CategoryGroupId') {
            data.Encounters[index].CptCodeId = null;
        }

        dispatch(setEncounterList(data));
    };

    const prepareBPDetail = (data: any) => {
        if (!isNursePractitioner) {
            let metricInfo = member?.HedisSummary?.find((h) => h.HedisMetricType === HedisMetricsTypeEnum.BP);
            if (Boolean(metricInfo) && metricInfo.IsApplicable) {
                const bpCptCodeList = data.Encounters.filter((e) => ['3074F', '3075F', '3078F', '3079F', '3077F', '3080F'].includes(e.CptCode));
                const hasInvalidDiagnosis = bpCptCodeList.some((ele) => {
                    const diagnoses = [
                        ele.PrimaryDiagnosis?.DiagnosisPointer?.Code,
                        ele.SecondaryDiagnosis?.DiagnosisPointer?.Code,
                        ele.TertiaryDiagnosis?.DiagnosisPointer?.Code,
                        ele.QuaternaryDiagnosis?.DiagnosisPointer?.Code,
                    ];
                    return !diagnoses.includes('I10');
                });

                if (hasInvalidDiagnosis) {
                    setDisableSubmitToClaim(true);
                } else {
                    setDisableSubmitToClaim(false);
                }
            }
        }
    };

    const handleOnChangeCptCode = (val: any, propName: string, index: number, cptCategoryId: string) => {
        let data = copyObject(encounterList);
        let filterCptCodeList = filteredCPTCodes(cptCategoryId);
        let cptCodeDate = filterCptCodeList.find((c) => c.Id === val);
        if (cptCodeDate) {
            data.Encounters[index]['CptCode'] = cptCodeDate.Code;
        }
        data.Encounters[index][propName] = val;
        if (propName === 'CptCodeId') {
            data.Encounters[index]['Units'] = 1;
            const autoModifiers = getAutoApplyModifiers(engagementDraft.MeansOfEngagement, cptCodeDate?.AllowedModifierCodes ?? []);
            const modifiers = [autoModifiers.Modifier, autoModifiers.Modifier2, '', ''].map((m) => m || '');
            const compacted = modifiers.filter(Boolean);
            const modifierFields = ['Modifier', 'Modifier2', 'Modifier3', 'Modifier4'];
            modifierFields.forEach((f, i) => {
                data.Encounters[index][f] = compacted[i] || '';
            });
        }

        if (propName === 'CategoryGroupId') {
            data.Encounters[index].CptCodeId = null;
        }

        prepareBPDetail(data);
        dispatch(setEncounterList(data));
    };

    const handleOnChangePrimaryDx = (val: ICD10, propName: string, index: number, dxIndex: number) => {
        const fieldValue = Boolean(val) ? `${val.Code} ${val.Name}` : '';
        let data = copyObject(encounterList);
        data.Encounters[index].DiagnosisList[dxIndex][propName] = fieldValue;
        const selectedDiagnosis = Boolean(val) ? diagnosys.find((d) => d.Code === val.Code) : null;
        data.Encounters[index].DiagnosisList[dxIndex].DiagnosisPointer = selectedDiagnosis;
        data.Encounters[index].DiagnosisList[dxIndex].DiagnosisFirstList = [];
        data.Encounters[index].DiagnosisList[dxIndex].DiagnosisSecondList = [];
        data.Encounters[index].DiagnosisList[dxIndex].DiagnosisThirdList = [];
        data.Encounters[index].DiagnosisList[dxIndex].DiagnosisFourthList = [];
        data.Encounters[index].DiagnosisList[dxIndex].FirstDiagnosis = null;
        data.Encounters[index].DiagnosisList[dxIndex].SecondDiagnosis = null;
        data.Encounters[index].DiagnosisList[dxIndex].ThirdDiagnosis = null;
        data.Encounters[index].DiagnosisList[dxIndex].FourthDiagnosis = null;

        prepareBPDetail(data);
        dispatch(setEncounterList(data, true));
    };

    const handleOnChangeDx = (val: any, propName: string, index: number, dxIndex: number, diagnosis?: ICD10) => {
        let data = copyObject(encounterList);
        data.Encounters[index].DiagnosisList[dxIndex][propName] = val;
        if (Boolean(diagnosis)) {
            data.Encounters[index].DiagnosisList[dxIndex].DiagnosisPointer = diagnosis;
        }

        if (propName === 'FirstDiagnosis') {
            data.Encounters[index].DiagnosisList[dxIndex].DiagnosisSecondList = [];
            data.Encounters[index].DiagnosisList[dxIndex].DiagnosisThirdList = [];
            data.Encounters[index].DiagnosisList[dxIndex].DiagnosisFourthList = [];
            data.Encounters[index].DiagnosisList[dxIndex].SecondDiagnosis = null;
            data.Encounters[index].DiagnosisList[dxIndex].ThirdDiagnosis = null;
            data.Encounters[index].DiagnosisList[dxIndex].FourthDiagnosis = null;
        }
        if (propName === 'SecondDiagnosis') {
            data.Encounters[index].DiagnosisList[dxIndex].DiagnosisThirdList = [];
            data.Encounters[index].DiagnosisList[dxIndex].DiagnosisFourthList = [];
            data.Encounters[index].DiagnosisList[dxIndex].ThirdDiagnosis = null;
            data.Encounters[index].DiagnosisList[dxIndex].FourthDiagnosis = null;
        }
        if (propName === 'ThridDiagnosis') {
            data.Encounters[index].DiagnosisList[dxIndex].DiagnosisFourthList = [];
            data.Encounters[index].DiagnosisList[dxIndex].FourthDiagnosis = null;
        }

        prepareBPDetail(data);
        dispatch(setEncounterList(data));
    };

    const handleOnChangeDefaultDiagnosis = (val: ICD10, nextListPropName: string) => {
        const fieldValue = Boolean(val) ? `${val.Code} ${val.Name}` : '';
        let data = copyObject(memberDefaultDiagnosis.DefaultDiagnosis) as DiagnosisDraft;
        data.Diagnosis = fieldValue;
        const selectedDiagnosis = Boolean(val) ? diagnosys.find((d) => d.Code === val.Code) : null;
        if (Boolean(selectedDiagnosis)) {
            data.DiagnosisPointer = {
                Id: selectedDiagnosis.Id,
                Name: selectedDiagnosis.Name,
                Code: selectedDiagnosis.Code,
                ICDcodeVersion: selectedDiagnosis.ICDcodeVersion,
                DiagnosisType: DiagnosisTypeEnum.Primary,
            } as DiagnosisPointer;
        } else {
            data.DiagnosisPointer = null;
        }
        data.DiagnosisFirstList = [];
        data.DiagnosisSecondList = [];
        data.DiagnosisThirdList = [];
        data.DiagnosisFourthList = [];
        data.FirstDiagnosis = null;
        data.SecondDiagnosis = null;
        data.ThirdDiagnosis = null;
        data.FourthDiagnosis = null;

        dispatch(setDefaultDiagnosis({ ...memberDefaultDiagnosis, DefaultDiagnosis: data }));
        if (Boolean(val?.Code)) {
            dispatch(getICD10DiagnosisByCategory(val.Code, 0, 0, nextListPropName, true));
        }
    };

    const handleOnChangeDifalultDx = (val: any, propName: string, nextListPropName: string = '') => {
        let data = copyObject(memberDefaultDiagnosis.DefaultDiagnosis) as DiagnosisDraft;
        data[propName] = val;

        if (propName === 'FirstDiagnosis') {
            data.DiagnosisSecondList = [];
            data.DiagnosisThirdList = [];
            data.DiagnosisFourthList = [];
            data.SecondDiagnosis = null;
            data.ThirdDiagnosis = null;
            data.FourthDiagnosis = null;
        }
        if (propName === 'SecondDiagnosis') {
            data.DiagnosisThirdList = [];
            data.DiagnosisFourthList = [];
            data.ThirdDiagnosis = null;
            data.FourthDiagnosis = null;
        }
        if (propName === 'ThridDiagnosis') {
            data.DiagnosisFourthList = [];
            data.FourthDiagnosis = null;
        }

        if (Boolean(nextListPropName)) {
            data[nextListPropName] = [];
            if (Boolean(val?.Code)) {
                dispatch(getICD10DiagnosisByCategory(val.Code, 0, 0, nextListPropName, true));
            }
        }
        dispatch(setDefaultDiagnosis({ ...memberDefaultDiagnosis, DefaultDiagnosis: data }));
    };

    const handleOnClickAdd = () => {
        let data = copyObject(encounterList);
        let tempId = (Math.random() + 1).toString(36).substring(7);
        data.Encounters.push({ tempId, DiagnosisList: [new DiagnosisDraft()] });
        dispatch(setEncounterList(data));
    };

    const handleOnClickRemove = (index) => {
        let data = copyObject(encounterList);
        data.Encounters = data.Encounters.filter((item, i) => i !== index);
        dispatch(setEncounterList(data));
    };

    const handleOnClickAddDiagnosis = (index: number) => {
        let data = copyObject(encounterList) as EncounterList;
        if ((data.Encounters[index].DiagnosisList?.length || 0) >= 4) {
            return;
        }
        if (data.Encounters[index].DiagnosisList?.some((d) => !d.Diagnosis)) {
            dispatch(setAssessmentError({ Show: true, Message: 'Please select a diagnosis' }));
            return;
        }
        data.Encounters[index].DiagnosisList.push(new DiagnosisDraft());
        dispatch(setEncounterList(data));
    };

    const handleOnClickRemoveDiagnosis = (index: number, dxIndex: number) => {
        let data = copyObject(encounterList);
        data.Encounters[index].DiagnosisList = data.Encounters[index].DiagnosisList.filter((_, i) => i !== dxIndex);
        dispatch(setEncounterList(data));
    };

    const validateCpt = () => {
        if (encounterList.Encounters.some((e) => !Boolean(e.CptCodeId))) {
            dispatch(setAssessmentError({ Show: true, Message: 'Please add a primary CPT code to all encounters before submitting.' }));
            return false;
        }

        if (encounterList.Encounters.some((e) => !Boolean(e.Units))) {
            dispatch(setAssessmentError({ Show: true, Message: "Please verify you've selected Units." }));
            return false;
        }

        return true;
    };

    const handleOnSave = (e, isReview = false, isHedisAlertReviewed = false) => {
        let hasDuplicate = hasDuplicateDiagnosisCodes(encounterList);
        if (hasDuplicate) {
            dispatch(setAssessmentError({ Show: true, Message: 'Duplicate diagnosis found.' }));
            return;
        }

        if (
            engagementDraft.AssessedRole?.RoleName === roleEnum.FIELD_ONBOARDING &&
            engagementDraft.CareCircleConfirmation?.CareCircleStatus !== StatusEnum.Completed &&
            engagementDraft.CareCircleStatus !== StatusEnum.Completed
        ) {
            dispatch(setShowMessage(true, 'Please complete Care Circle before submitting or saving for review.', 'error'));
            return;
        }

        if (encounterList.Encounters.some)
            if (!memberBasicInfo.Gender || memberBasicInfo.Gender === genders.find((x) => x.value === '0').label) {
                dispatch(setAssessmentError({ Show: true, Message: 'Please select a Male or Female gender for claim submission.' }));
                return;
            }

        if (!validateCpt()) {
            return;
        } else if (!isReview) {
            setShowCptErrorMsg(false);
            if (
                authData.Permissions.includes('PHQ9 Score Greater Than 14 Alert') &&
                encounterList.Encounters.some((en) => !Boolean(en.PrimaryDiagnosis?.Diagnosis))
            ) {
                dispatch(setAssessmentError({ Show: true, Message: 'Please select a diagnosis' }));
                return;
            }
        }
        let isDiagnosisMissed = false;
        encounterList.Encounters.forEach((encounter) => {
            encounter.DiagnosisList.forEach((diagnosis) => {
                if (Boolean(diagnosis.DiagnosisFirstList?.length) && !Boolean(diagnosis.FirstDiagnosis)) {
                    isDiagnosisMissed = true;
                }
                if (Boolean(diagnosis.DiagnosisSecondList?.length) && !Boolean(diagnosis.SecondDiagnosis)) {
                    isDiagnosisMissed = true;
                }
                if (Boolean(diagnosis.DiagnosisThirdList?.length) && !Boolean(diagnosis.ThirdDiagnosis)) {
                    isDiagnosisMissed = true;
                }
                if (Boolean(diagnosis.DiagnosisFourthList?.length) && !Boolean(diagnosis.FourthDiagnosis)) {
                    isDiagnosisMissed = true;
                }
            });
        });

        if (!isReview && isDiagnosisMissed) {
            setShowDxError(true);
            dispatch(setAssessmentError({ Show: true, Message: 'Please select a diagnosis' }));
            return;
        }

        let canSubmit = true;
        let encounters = [] as MemberEncounter[];
        let bpCptList = ['3074F', '3075F', '3078F', '3079F', '3077F', '3080F'];
        let hgbA1cCptList = ['3051F', '3044F', '3046F', '3052F'];
        let lipidBhdxCptList = ['3048F', '3049F', '3050F', '80061', '83700', '83701', '83704', '83721'];
        let hgbA1cBhdxCptList = ['3051F', '3044F', '3046F', '3052F'];
        let bpIcdCodes = ['I10'];
        let hgbA1cIcdCodes = ['E10', 'E11'];
        let bhdxIcdCodes = ['F20.9', 'F25.0', 'F25.9', 'F31.32', 'F31.2', 'F31.3', 'F31.81', 'F31.89', 'F31.9'];

        let erroMessages = [];

        encounterList.Encounters.forEach((encounter) => {
            const cptCodeDetail = prepareCPTCodes(encounter.CategoryGroupId).find((c) => c.Id === encounter.CptCodeId);
            let selectedIcd10Codes = [];
            var primaryDiagPointer = [] as DiagnosisPointer[];
            if (Boolean(encounter.PrimaryDiagnosis)) {
                if (Boolean(encounter.PrimaryDiagnosis?.DiagnosisPointer?.Id)) {
                    primaryDiagPointer.push(encounter.PrimaryDiagnosis.DiagnosisPointer);
                    selectedIcd10Codes.push(encounter.PrimaryDiagnosis.DiagnosisPointer.Code);
                }
                if (Boolean(encounter.PrimaryDiagnosis?.FirstDiagnosis?.Id)) {
                    primaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.PrimaryDiagnosis.FirstDiagnosis));
                    selectedIcd10Codes.push(encounter.PrimaryDiagnosis.FirstDiagnosis.Code);
                }
                if (Boolean(encounter.PrimaryDiagnosis?.SecondDiagnosis?.Id)) {
                    primaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.PrimaryDiagnosis.SecondDiagnosis));
                    selectedIcd10Codes.push(encounter.PrimaryDiagnosis.SecondDiagnosis.Code);
                }
                if (Boolean(encounter.PrimaryDiagnosis?.ThirdDiagnosis?.Id)) {
                    primaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.PrimaryDiagnosis.ThirdDiagnosis));
                    selectedIcd10Codes.push(encounter.PrimaryDiagnosis.ThirdDiagnosis.Code);
                }
                if (Boolean(encounter.PrimaryDiagnosis?.FourthDiagnosis?.Id)) {
                    primaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.PrimaryDiagnosis.FourthDiagnosis));
                    selectedIcd10Codes.push(encounter.PrimaryDiagnosis.FourthDiagnosis.Code);
                }
            } else {
                canSubmit = false;
            }

            var secondaryDiagPointer = [] as DiagnosisPointer[];
            if (Boolean(encounter.SecondaryDiagnosis)) {
                if (Boolean(encounter.SecondaryDiagnosis?.DiagnosisPointer?.Id)) {
                    secondaryDiagPointer.push(encounter.SecondaryDiagnosis.DiagnosisPointer);
                    selectedIcd10Codes.push(encounter.SecondaryDiagnosis.DiagnosisPointer.Code);
                }
                if (Boolean(encounter.SecondaryDiagnosis?.FirstDiagnosis?.Id)) {
                    secondaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.SecondaryDiagnosis.FirstDiagnosis));
                    selectedIcd10Codes.push(encounter.SecondaryDiagnosis.FirstDiagnosis.Code);
                }
                if (Boolean(encounter.SecondaryDiagnosis?.SecondDiagnosis?.Id)) {
                    secondaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.SecondaryDiagnosis.SecondDiagnosis));
                    selectedIcd10Codes.push(encounter.SecondaryDiagnosis.SecondDiagnosis.Code);
                }
                if (Boolean(encounter.SecondaryDiagnosis?.ThirdDiagnosis?.Id)) {
                    secondaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.SecondaryDiagnosis.ThirdDiagnosis));
                    selectedIcd10Codes.push(encounter.SecondaryDiagnosis.ThirdDiagnosis.Code);
                }
                if (Boolean(encounter.SecondaryDiagnosis?.FourthDiagnosis?.Id)) {
                    secondaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.SecondaryDiagnosis.FourthDiagnosis));
                    selectedIcd10Codes.push(encounter.SecondaryDiagnosis.FourthDiagnosis.Code);
                }
            }

            var tertiaryDiagPointer = [] as DiagnosisPointer[];
            if (Boolean(encounter.TertiaryDiagnosis)) {
                if (Boolean(encounter.TertiaryDiagnosis?.DiagnosisPointer?.Id)) {
                    tertiaryDiagPointer.push(encounter.TertiaryDiagnosis.DiagnosisPointer);
                    selectedIcd10Codes.push(encounter.TertiaryDiagnosis.DiagnosisPointer.Code);
                }
                if (Boolean(encounter.TertiaryDiagnosis?.FirstDiagnosis?.Id)) {
                    tertiaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.TertiaryDiagnosis.FirstDiagnosis));
                    selectedIcd10Codes.push(encounter.TertiaryDiagnosis.FirstDiagnosis.Code);
                }
                if (Boolean(encounter.TertiaryDiagnosis?.SecondDiagnosis?.Id)) {
                    tertiaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.TertiaryDiagnosis.SecondDiagnosis));
                    selectedIcd10Codes.push(encounter.TertiaryDiagnosis.SecondDiagnosis.Code);
                }
                if (Boolean(encounter.TertiaryDiagnosis?.ThirdDiagnosis?.Id)) {
                    tertiaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.TertiaryDiagnosis.ThirdDiagnosis));
                    selectedIcd10Codes.push(encounter.TertiaryDiagnosis.ThirdDiagnosis.Code);
                }
                if (Boolean(encounter.TertiaryDiagnosis?.FourthDiagnosis?.Id)) {
                    tertiaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.TertiaryDiagnosis.FourthDiagnosis));
                    selectedIcd10Codes.push(encounter.TertiaryDiagnosis.FourthDiagnosis.Code);
                }
            }

            var quaternaryDiagPointer = [] as DiagnosisPointer[];
            if (Boolean(encounter.QuaternaryDiagnosis)) {
                if (Boolean(encounter.QuaternaryDiagnosis?.DiagnosisPointer?.Id)) {
                    quaternaryDiagPointer.push(encounter.QuaternaryDiagnosis.DiagnosisPointer);
                    selectedIcd10Codes.push(encounter.QuaternaryDiagnosis.DiagnosisPointer.Code);
                }
                if (Boolean(encounter.QuaternaryDiagnosis?.FirstDiagnosis?.Id)) {
                    quaternaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.QuaternaryDiagnosis.FirstDiagnosis));
                    selectedIcd10Codes.push(encounter.QuaternaryDiagnosis.FirstDiagnosis.Code);
                }
                if (Boolean(encounter.QuaternaryDiagnosis?.SecondDiagnosis?.Id)) {
                    quaternaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.QuaternaryDiagnosis.SecondDiagnosis));
                    selectedIcd10Codes.push(encounter.QuaternaryDiagnosis.SecondDiagnosis.Code);
                }
                if (Boolean(encounter.QuaternaryDiagnosis?.ThirdDiagnosis?.Id)) {
                    quaternaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.QuaternaryDiagnosis.ThirdDiagnosis));
                    selectedIcd10Codes.push(encounter.QuaternaryDiagnosis.ThirdDiagnosis.Code);
                }
                if (Boolean(encounter.QuaternaryDiagnosis?.FourthDiagnosis?.Id)) {
                    quaternaryDiagPointer.push(convertICD10ToDiagnosisPointer(encounter.QuaternaryDiagnosis.FourthDiagnosis));
                    selectedIcd10Codes.push(encounter.QuaternaryDiagnosis.FourthDiagnosis.Code);
                }
            }

            var additionalDiagnosis = [] as DiagnosisPointer[];
            if (Boolean(encounter.AdditionalDiagnosis)) {
                encounter.AdditionalDiagnosis.forEach((diagnosis) => {
                    let diagnosisPointer = {} as DiagnosisPointer;
                    if (diagnosis.FourthDiagnosis != null) {
                        diagnosisPointer = convertICD10ToDiagnosisPointer(diagnosis.FourthDiagnosis);
                    } else if (diagnosis.ThirdDiagnosis != null) {
                        diagnosisPointer = convertICD10ToDiagnosisPointer(diagnosis.ThirdDiagnosis);
                    } else if (diagnosis.SecondDiagnosis != null) {
                        diagnosisPointer = convertICD10ToDiagnosisPointer(diagnosis.SecondDiagnosis);
                    } else if (diagnosis.FirstDiagnosis != null) {
                        diagnosisPointer = convertICD10ToDiagnosisPointer(diagnosis.FirstDiagnosis);
                    } else {
                        diagnosisPointer = diagnosis.DiagnosisPointer;
                    }
                    additionalDiagnosis.push(diagnosisPointer);
                });
            }

            if (Boolean(cptCodeDetail)) {
                if (bpCptList.some((c) => c === cptCodeDetail.Code) && !bpIcdCodes.some((i) => selectedIcd10Codes.includes(i))) {
                    erroMessages.push(`BP CPT ${cptCodeDetail.Code} requires the I10 dx for BP HEDIS metric.`);
                }
                if (hgbA1cCptList.some((c) => c === cptCodeDetail.Code) && !hgbA1cIcdCodes.some((i) => selectedIcd10Codes.includes(i))) {
                    erroMessages.push(`HgbA1c CPT ${cptCodeDetail.Code} requires any of E10 or E11 dx for HgbA1C HEDIS metric.`);
                }
                if (lipidBhdxCptList.some((c) => c === cptCodeDetail.Code) && !bhdxIcdCodes.some((i) => selectedIcd10Codes.includes(i))) {
                    erroMessages.push(
                        `Lipid CPT ${cptCodeDetail.Code} requires any of F20.9, F25.0, F25.9, F31.32, F31.2, F31.3, F31.81, F31.89, F31.9 dx for Lipid for BH DX HEDIS metric.`
                    );
                }
                if (hgbA1cBhdxCptList.some((c) => c === cptCodeDetail.Code) && !bhdxIcdCodes.some((i) => selectedIcd10Codes.includes(i))) {
                    erroMessages.push(
                        `HgbA1c for BH CPT ${cptCodeDetail.Code} requires any of F20.9, F25.0, F25.9, F31.32, F31.2, F31.3, F31.81, F31.89, F31.9 dx for HgbA1C for BH DX HEDIS
                            metric.`
                    );
                }
            }

            var encounterDetails = {} as MemberEncounter;
            encounterDetails.Id = encounter.Id;
            encounterDetails.MemberId = memberId;
            encounterDetails.PlaceOfEncounterValue = getEncounterPlace(engagementDraft.MeansOfEngagement)?.Value;
            encounterDetails.PlaceOfEncounterId = getEncounterPlace(engagementDraft.MeansOfEngagement)?.Id;
            encounterDetails.CategoryGroupId = encounter.CategoryGroupId;
            encounterDetails.Units = Boolean(encounter.Units) ? encounter.Units : 1;
            encounterDetails.CPTcode = cptCodeDetail;
            encounterDetails.PrimaryDiagnosisList = primaryDiagPointer.length > 0 ? primaryDiagPointer : null;
            encounterDetails.SecondaryDiagnosisList = secondaryDiagPointer.length > 0 ? secondaryDiagPointer : null;
            encounterDetails.TertiaryDiagnosisList = tertiaryDiagPointer.length > 0 ? tertiaryDiagPointer : null;
            encounterDetails.QuaternaryDiagnosisList = quaternaryDiagPointer.length > 0 ? quaternaryDiagPointer : null;
            const visitedDate = toDate(engagementDraft.VisitedTime);
            encounterDetails.ServiceDate = visitedDate ? formatDate(visitedDate, 'MM/dd/yyyy hh:mm') : '';
            encounterDetails.AdditionalDiagnosisList = additionalDiagnosis;
            encounterDetails.IsSummaryNote = encounter.IsSummaryNote;
            encounterDetails.EncounterStatus = isReview ? EncounterStatusEnum.ForReview : EncounterStatusEnum.ToSubmit;
            encounterDetails.Modifier = encounter.Modifier || '';
            encounterDetails.Modifier2 = encounter.Modifier2 || '';
            encounterDetails.Modifier3 = encounter.Modifier3 || '';
            encounterDetails.Modifier4 = encounter.Modifier4 || '';

            encounters.push(encounterDetails);
        });

        if (!isReview && !canSubmit) {
            dispatch(setAssessmentError({ Show: true, Message: 'Please add a primary diagnosis to all encounters before submitting.' }));
            return;
        } else if (encounters.some((e) => !Boolean(e.CPTcode))) {
            dispatch(setAssessmentError({ Show: true, Message: 'Please add a primary CPT code to all encounters before submitting.' }));
            return;
        }

        if (erroMessages.length > 0 && !isHedisAlertReviewed) {
            setEventValue(e);
            setIsReviewValue(isReview);
            setHedisAlertMessage(erroMessages);
            setShowHedisAlert(true);
            return;
        }

        if (!noAppointments) {
            if (engagementDraft.Contacted) {
                dispatch(updateAppointmentIdListToStatus(AppointmentStatusEnum.Completed, null));
            } else {
                dispatch(updateAppointmentIdListToStatus(AppointmentStatusEnum.NoCallOrNoShow, null));
            }
        }

        if (notAssessment) {
            dispatch(insertEncountersNotAsssessment({ MemberEncounter: encounters }, false, finalizeSignOff));
        } else {
            dispatch(
                insertEncounters({ MemberEncounter: encounters }, false, () => {
                    navigateToDocumentEngagement();
                })
            );
        }
        dispatch(addCompletedAssessmentTimelines(engagementDraft, memberId));
        setClickedBtnId(e.target.id);
    };

    const openSubmissionSummaryModal = (e: React.MouseEvent<HTMLButtonElement>, isReview = false) => {
        setPendingSubmissionAction({
            isReview,
            buttonId: e?.currentTarget?.id || (isReview ? 'btn-review' : 'btn-claim'),
        });
        setShowSubmissionSummaryModal(true);
    };

    const closeSubmissionSummaryModal = () => {
        setShowSubmissionSummaryModal(false);
        setPendingSubmissionAction(null);
    };

    const confirmSubmissionSummary = () => {
        if (!pendingSubmissionAction) {
            setShowSubmissionSummaryModal(false);
            return;
        }

        const eventShim = {
            target: {
                id: pendingSubmissionAction.buttonId,
            },
        };

        setShowSubmissionSummaryModal(false);
        setPendingSubmissionAction(null);
        handleOnSave(eventShim, pendingSubmissionAction.isReview);
    };

    const navigateToDocumentEngagement = () => {
        dispatch(resetAssessment());
        dispatch(resetHraAssessment());
        dispatch(resetFuhFumAssessment());
        dispatch(resetDocumentEngagement());
        navigate(`/provider/members-list/member-chart/${memberId}/assessment-dashboard`);
    };

    const getAutoApplyModifiers = (meansOfEngagement: MeansOfEngagementEnum, allowedModifierCodes: string[] = []) => {
        let degreeModifier: string = null;
        let telehealthModifier: string = null;

        const roleName = currentProvider?.Role?.RoleName;
        const isNpOrBhs = roleName === roleEnum.NURSE_PRACTITIONER || roleName === roleEnum.BEHAVIOURAL_HEALTH_SPECIALIST;
        const degree = currentProvider?.Degree ?? DegreeEnum.None;

        if (isNpOrBhs || degree === DegreeEnum.Masters || degree === DegreeEnum.Doctorate) {
            degreeModifier = 'HO';
        } else if (!isNpOrBhs && degree === DegreeEnum.Bachelors) {
            degreeModifier = 'HN';
        }

        if (meansOfEngagement === MeansOfEngagementEnum.Telemedicine) {
            telehealthModifier = 'GT';
        } else if (meansOfEngagement === MeansOfEngagementEnum.Phone) {
            telehealthModifier = 'FQ';
        }

        if (allowedModifierCodes.length === 0) {
            return { Modifier: null, Modifier2: null };
        }

        if (degreeModifier && !allowedModifierCodes.includes(degreeModifier)) {
            degreeModifier = null;
        }
        if (telehealthModifier && !allowedModifierCodes.includes(telehealthModifier)) {
            telehealthModifier = null;
        }

        return { Modifier: degreeModifier, Modifier2: telehealthModifier };
    };

    const getEncounterPlace = (meansOfEngagement: MeansOfEngagementEnum) => {
        switch (meansOfEngagement) {
            case MeansOfEngagementEnum.InPerson:
                return placesOfEncounters.find((p) => p.Name === Home);
            case MeansOfEngagementEnum.Phone:
                return placesOfEncounters.find((p) => p.Name === Phone);
            case MeansOfEngagementEnum.Telemedicine:
                return placesOfEncounters.find((p) => p.Name === Telemedicine);
            case MeansOfEngagementEnum.Email:
                return placesOfEncounters.find((p) => p.Name === OfficeVisit);
            case MeansOfEngagementEnum.Office:
                return placesOfEncounters.find((p) => p.Name === OfficeVisit);
            case MeansOfEngagementEnum.TextMessage:
                return placesOfEncounters.find((p) => p.Name === OfficeVisit);
            default:
                return placesOfEncounters.find((p) => p.Name === Home);
        }
    };

    const convertICD10ToDiagnosisPointer = (data: ICD10) => {
        return {
            Id: data.Id,
            Name: data.Name,
            ICDcodeVersion: data.ICDcodeVersion,
            Code: data.Code,
        } as DiagnosisPointer;
    };

    const handleOnSkipEncounters = (e) => {
        if (noAppointments || notAssessment) {
            dispatch(insertEncountersNotAsssessment({}, true, finalizeSignOff));
        } else {
            let statusChange = AppointmentStatusEnum.Completed;
            if (!engagementDraft.Contacted) {
                statusChange = AppointmentStatusEnum.NoCallOrNoShow;
            }
            dispatch(
                updateAppointmentIdListToStatus(statusChange, () => {
                    dispatch(
                        insertEncounters({}, true, () => {
                            navigateToDocumentEngagement();
                        })
                    );
                })
            );
        }

        setClickedBtnId(e.target.id);
    };

    const prepareDiagnosis = (index) => {
        return [{ Id: diagnosisFromClaimsId, Code: 'Add Diagnosis from Claims', Name: '' }, ...diagnosys];
    };

    const handleOnClickSetDefaultPrimary = (index: number) => {
        let data = copyObject(encounterList);
        data.Encounters[index].DiagnosisList[0] = memberDefaultDiagnosis.DefaultDiagnosis;
        prepareBPDetail(data);
        dispatch(setEncounterList(data));
    };

    const handleOnChangeSelect = (val, index, dxIndex, bindResponsePropName) => {
        if (Boolean(val)) {
            dispatch(getICD10DiagnosisByCategory(val.Code, index, dxIndex, bindResponsePropName));
        }
    };

    const prepareWarnings = () => {
        const noteCounts = engagementDraft?.SummaryNotes?.filter((s) => s.IsConfirm)?.length || 0;
        const discharges = memberTransitionalCare?.TransitionSteps?.filter((step) => step.TransitionStepType === 'Discharge');
        const isDisCharged = Boolean(discharges.length > 0) && memberBasicInfo?.IsInTransition;

        const assessmentDetails: string[] = [];

        if (!engagementDraft.ConsentFormId) {
            assessmentDetails.push('Consent form is not completed');
        }

        const menuMap = new Map(assessmentMenuList.map((m) => [m.Name, m]));
        const hasMenu = (name: string) => menuMap.has(name);
        const isPending = (name: string) => menuMap.get(name)?.IsPending;
        const isExpired = (name: string) => menuMap.get(name)?.IsExpired;
        const isCompleted = (name: string) => menuMap.get(name)?.IsCompleted;

        if (hasMenu(assessmentMenuEnum.VITAL_SIGNS) && !isCompleted(assessmentMenuEnum.VITAL_SIGNS)) {
            assessmentDetails.push('Vital Signs is not completed');
        }

        if (hasMenu(assessmentMenuEnum.CARE_PLANS)) {
            if (memberAssessmentHistory.ShowCarePlanAlert) {
                assessmentDetails.push('Plans are expired');
            } else if (engagementDraft.GoalStatus === assessmentStatusEnum.IN_PROGRESS) {
                assessmentDetails.push('Set goal is not completed');
            }
            if (isPending(assessmentMenuEnum.CARE_PLANS)) {
                assessmentDetails.push('Integrated care plan are not completed');
            }
        }

        if (hasMenu(assessmentMenuEnum.PRAPARE_SDOH) && (isPending(assessmentMenuEnum.PRAPARE_SDOH) || isExpired(assessmentMenuEnum.PRAPARE_SDOH))) {
            assessmentDetails.push('SDOH is not completed');
        }

        if (hasMenu(assessmentMenuEnum.PAST_PRESENT_HISTORY) && isPending(assessmentMenuEnum.PAST_PRESENT_HISTORY)) {
            assessmentDetails.push('Past present history is not completed');
        }

        if (hasMenu(assessmentMenuEnum.PAST_SURGICAL_HISTORY) && isPending(assessmentMenuEnum.PAST_SURGICAL_HISTORY)) {
            assessmentDetails.push('Past surgical history is not completed');
        }

        if (hasMenu(assessmentMenuEnum.REVIEW_SYSTEMS) && isPending(assessmentMenuEnum.REVIEW_SYSTEMS)) {
            assessmentDetails.push('Review system is not completed');
        }

        if (hasMenu(assessmentMenuEnum.PHYSICAL_EXAM) && isPending(assessmentMenuEnum.PHYSICAL_EXAM)) {
            assessmentDetails.push('Physical exam is not completed');
        }

        if (hasMenu(assessmentMenuEnum.PHQ9) && (isPending(assessmentMenuEnum.PHQ9) || isExpired(assessmentMenuEnum.PHQ9))) {
            assessmentDetails.push('PHQ9 is not completed');
        }

        if (
            hasMenu(assessmentMenuEnum.HGBA1C_ASSESSMENT) &&
            (isPending(assessmentMenuEnum.HGBA1C_ASSESSMENT) || (memberAssessmentHistory.IsHgbA1CNeedsReview && authData.Role === roleEnum.NURSE_PRACTITIONER))
        ) {
            assessmentDetails.push('HgbA1C assessment is not completed');
        }

        if (hasMenu(assessmentMenuEnum.DIABETES_FOOT) && isPending(assessmentMenuEnum.DIABETES_FOOT)) {
            assessmentDetails.push('Diabetes foot exam is not completed');
        }

        if (
            hasMenu(assessmentMenuEnum.URINALYSIS_REPORT) &&
            (isPending(assessmentMenuEnum.URINALYSIS_REPORT) ||
                (memberAssessmentHistory.IsUrinalysisNeedsReview && authData.Role === roleEnum.NURSE_PRACTITIONER))
        ) {
            assessmentDetails.push('Urinalysis report is not completed');
        }

        if (
            hasMenu(assessmentMenuEnum.ALLERGIES) &&
            (isPending(assessmentMenuEnum.ALLERGIES) || (memberAllergies.length === 0 && memberBasicInfo.NoKnownAllergies === false))
        ) {
            assessmentDetails.push('Allergy is not completed');
        }

        if (hasMenu(assessmentMenuEnum.MINI_MENTAL_STATUS_EXAM) && isPending(assessmentMenuEnum.MINI_MENTAL_STATUS_EXAM)) {
            assessmentDetails.push('Mini mental exam is not completed');
        }

        if (hasMenu(assessmentMenuEnum.SUBJECTIVE_ASSESSMENT) && isPending(assessmentMenuEnum.SUBJECTIVE_ASSESSMENT)) {
            assessmentDetails.push('Subjective assessment is not completed');
        }

        if (hasMenu(assessmentMenuEnum.INTERVENTIONS) && isPending(assessmentMenuEnum.INTERVENTIONS)) {
            assessmentDetails.push('Intervention is not completed');
        }

        if (hasMenu(assessmentMenuEnum.OTHER_FACTORS_AND_RISKS) && isPending(assessmentMenuEnum.OTHER_FACTORS_AND_RISKS)) {
            assessmentDetails.push('Factor and risk assessment is not completed');
        }

        if (hasMenu(assessmentMenuEnum.REQUESTS_REFERRALS) && isPending(assessmentMenuEnum.REQUESTS_REFERRALS)) {
            assessmentDetails.push('Request is not completed');
        }

        if (hasMenu(assessmentMenuEnum.FOLLOW_UP) && isPending(assessmentMenuEnum.FOLLOW_UP)) {
            assessmentDetails.push('Follow up assessment is not completed');
        }

        if (hasMenu(assessmentMenuEnum.EDUCATION) && isPending(assessmentMenuEnum.EDUCATION)) {
            assessmentDetails.push('Education is not completed');
        }

        if (hasMenu(assessmentMenuEnum.DEMOGRAPHICS) && isPending(assessmentMenuEnum.DEMOGRAPHICS)) {
            assessmentDetails.push('Demographic info is not completed');
        }

        if (hasMenu(assessmentMenuEnum.HEALTH_PLAN) && isPending(assessmentMenuEnum.HEALTH_PLAN)) {
            assessmentDetails.push('Health plan is not completed');
        }

        if (hasMenu(assessmentMenuEnum.CARE_CIRCLE) && engagementDraft.CareCircleStatus === assessmentStatusEnum.IN_PROGRESS) {
            assessmentDetails.push('Care circle is not completed');
        }

        if (hasMenu(assessmentMenuEnum.PCP_SPECIALIST) && isPending(assessmentMenuEnum.PCP_SPECIALIST)) {
            assessmentDetails.push('Pcp and specialist is not completed');
        }

        if (hasMenu(assessmentMenuEnum.TABLETS) && isPending(assessmentMenuEnum.TABLETS)) {
            assessmentDetails.push('Tablet is not completed');
        }

        if (
            hasMenu(assessmentMenuEnum.MEDICATION_RECONCILIATION) &&
            (isPending(assessmentMenuEnum.MEDICATION_RECONCILIATION) || (isDisCharged && !isCompleted(assessmentMenuEnum.MEDICATION_RECONCILIATION)))
        ) {
            assessmentDetails.push('Medication reconciliation is not completed');
        }

        if (hasMenu(assessmentMenuEnum.PROGRESS_NOTES)) {
            if (!isCompleted(assessmentMenuEnum.PROGRESS_NOTES)) {
                assessmentDetails.push('Progress notes are not completed');
            }
            if (!engagementDraft.FollowupRecommendations) {
                assessmentDetails.push('Follow up recommendations are not completed');
            }
        }

        if (hasMenu(assessmentMenuEnum.TOBACCO_AND_DRUG) && !engagementDraft.TobaccoAndDrugAssessmentId) {
            if (tobaccoAndDrug?.Score >= 3 || tobaccoAndDrug?.AuditAssessment?.Score >= 8) {
                assessmentDetails.push(`${assessmentMenuEnum.TOBACCO_AND_DRUG} assessment needs to be reviewed within 30 days.`);
            } else if (tobaccoAndDrug?.Score <= 3 || tobaccoAndDrug?.AuditAssessment?.Score <= 8) {
                assessmentDetails.push(`The provider should be alerted to do the ${assessmentMenuEnum.TOBACCO_AND_DRUG} assessment again in 90 days`);
            }
        }

        if (noteCounts === 0) {
            assessmentDetails.push('Summary notes are not selected');
        }

        setAssessmentWarnings(assessmentDetails);
    };

    const handleOnSelectDxFromClaims = (val: any, index: number, dxIndex: number) => {
        dispatch(prepareICD10Hierarchy(val, index, dxIndex));
    };

    const renderDiagnosisList = (title: string, diagnosises: ICD10[], selectedDiagnosis: string[]) => {
        if (!Boolean(diagnosises?.length)) {
            return null;
        }

        return (
            <Stack spacing={2} marginBottom={2}>
                <Typography variant="subtitle2" component="label">
                    {title}
                </Typography>
                <Box>
                    {diagnosises.map((diagnosis, index) => (
                        <Chip
                            key={`diagnosis-${diagnosis.Id}-${index}`}
                            sx={{ mb: 2, mr: 2 }}
                            disabled={selectedDiagnosis.some((s) => s === `${diagnosis.Code.replaceAll('.', '')}`)}
                            onClick={() => {
                                handleOnSelectDxFromClaims(diagnosis.Code, diagnosisFromClaims.Index, diagnosisFromClaims.DxIndex);
                                setDiagnosisFromClaims(new DiagnosisFromClaimsState());
                            }}
                            label={`${diagnosis.Code} ${diagnosis.Name}`}
                            color="primary"
                            variant="outlined"
                        />
                    ))}
                </Box>
            </Stack>
        );
    };

    const renderDiagnosisFromClaimsModal = () => {
        const selectedDiagnosis = [];
        encounterList.Encounters[diagnosisFromClaims.Index]?.DiagnosisList.forEach((d) => selectedDiagnosis.push(d.Diagnosis?.substring(0, 3)));

        encounterList.Encounters[diagnosisFromClaims.Index]?.DiagnosisList.forEach((d) => selectedDiagnosis.push(d.DiagnosisPointer?.Code.replaceAll('.', '')));

        encounterList.Encounters[diagnosisFromClaims.Index]?.DiagnosisList.forEach((d) => selectedDiagnosis.push(d.FirstDiagnosis?.Code.replaceAll('.', '')));

        encounterList.Encounters[diagnosisFromClaims.Index]?.DiagnosisList.forEach((d) => selectedDiagnosis.push(d.SecondDiagnosis?.Code.replaceAll('.', '')));

        encounterList.Encounters[diagnosisFromClaims.Index]?.DiagnosisList.forEach((d) => selectedDiagnosis.push(d.ThirdDiagnosis?.Code.replaceAll('.', '')));

        encounterList.Encounters[diagnosisFromClaims.Index]?.DiagnosisList.forEach((d) => selectedDiagnosis.push(d.FourthDiagnosis?.Code.replaceAll('.', '')));

        return (
            <Dialog
                open={diagnosisFromClaims.Show}
                maxWidth={false}
                PaperProps={{
                    sx: { alignSelf: 'flex-start' },
                }}
            >
                <DialogTitle>Select Diagnosis</DialogTitle>
                <DialogContent dividers sx={{ width: '800px', maxWidth: '100%' }}>
                    {isFetching || isDiagnosisFetching ? (
                        <Box display="flex" justifyContent="center" className="loader-container">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <React.Fragment>
                            {renderDiagnosisList('Clinical Data', diagnosisesByFilter, selectedDiagnosis)}
                            {renderDiagnosisList('Past Encounters', memberPastDiagnosysByFilter, selectedDiagnosis)}
                            {renderDiagnosisList('Claims', claimsDiagnosisByFilter, selectedDiagnosis)}
                        </React.Fragment>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button size="small" type="button" onClick={() => setDiagnosisFromClaims(new DiagnosisFromClaimsState())} variant="outlined">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const handleOnCloseDefaultDiagnosis = () => {
        setShowDefaultDiagnosisModal(false);
        dispatch(getDefaultDiagnosis(memberId));
    };

    const handleSubmitDefaultDiagnosis = (values: DiagnosisDraft) => {
        dispatch(
            updateDefaultDiagnosis({ ...memberDefaultDiagnosis, DefaultDiagnosis: values, MemberId: memberId }, () => {
                setShowDefaultDiagnosisModal(false);
            })
        );
    };

    const renderDefaultPrimaryDiagnosis = () => {
        const inputName = 'default-primary-auto-complete';

        return (
            <Dialog sx={classes.form} open={showDefaultDiagnosisModal} onClose={handleOnCloseDefaultDiagnosis} scroll="paper">
                <Formik
                    initialValues={memberDefaultDiagnosis.DefaultDiagnosis}
                    onSubmit={handleSubmitDefaultDiagnosis}
                    validationSchema={defaultDiagnosisSchema}
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue, errors, handleChange, handleBlur, submitCount }: FormikProps<DiagnosisDraft>) => (
                        <Form>
                            <DialogTitle id="scroll-dialog-drafts" display="flex" alignItems="center" justifyContent="space-between">
                                <span>Set Default Primary Diagnosis</span>
                            </DialogTitle>
                            <DialogContent dividers={true} style={{ width: '500px' }}>
                                <Autocomplete
                                    id={`primary-auto-select-default`}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option.Name === value.Name}
                                    getOptionLabel={(option: any) => (typeof option === 'object' ? `${option.Code} ${option.Name}` : option || '')}
                                    value={values.Diagnosis as any}
                                    options={diagnosys || []}
                                    loading={isFetchingDisgnosys && focusInputName === inputName}
                                    freeSolo
                                    onChange={(e, value: any) => handleOnChangeDefaultDiagnosis(value, 'DiagnosisFirstList')}
                                    PopperComponent={PopperMy}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Diagnosis"
                                            required
                                            variant="outlined"
                                            onChange={(e) => {
                                                dispatch(getICD10DiagnosisByKeyword(e.target.value, engagementDraft?.AssessedRole?.Id));
                                            }}
                                            onFocus={() => setFocusInputName(inputName)}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {isFetchingDisgnosys && focusInputName === inputName ? (
                                                            <CircularProgress color="inherit" size={20} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                            error={Boolean(errors.Diagnosis) && Boolean(submitCount)}
                                        />
                                    )}
                                />
                                {Boolean(values.DiagnosisFirstList?.length) ? (
                                    <FormControl fullWidth error={Boolean(errors.FirstDiagnosis || errors.FirstDiagnosis?.Code) && Boolean(submitCount)}>
                                        <Select
                                            fullWidth
                                            id={`default-primary-first-type-select`}
                                            value={Boolean(values.FirstDiagnosis?.Code) ? values.FirstDiagnosis.Code : 0}
                                            onChange={(e) => {
                                                const selectedDiagnosis = values.DiagnosisFirstList.find((d) => d.Code === e.target.value);
                                                handleOnChangeDifalultDx(selectedDiagnosis, 'FirstDiagnosis', 'DiagnosisSecondList');
                                            }}
                                            error={Boolean(errors.FirstDiagnosis || errors.FirstDiagnosis?.Code) && Boolean(submitCount)}
                                        >
                                            <MenuItem value={0}>Select Diagnosis</MenuItem>
                                            {values.DiagnosisFirstList.map((category) => (
                                                <MenuItem
                                                    key={`menu_item_${category.Code}`}
                                                    value={category.Code}
                                                >{`${category.Code} ${category.Name}`}</MenuItem>
                                            ))}
                                        </Select>
                                        {Boolean(errors.FirstDiagnosis || errors.FirstDiagnosis?.Code) && Boolean(submitCount) ? (
                                            <FormHelperText error>Diagnosis is required.</FormHelperText>
                                        ) : null}
                                    </FormControl>
                                ) : null}
                                {Boolean(values.DiagnosisSecondList?.length) ? (
                                    <FormControl fullWidth error={Boolean(errors.SecondDiagnosis || errors.SecondDiagnosis?.Code) && Boolean(submitCount)}>
                                        <Select
                                            fullWidth
                                            id={`default-primary-second-select`}
                                            value={Boolean(values.SecondDiagnosis?.Code) ? values.SecondDiagnosis.Code : 0}
                                            onChange={(e) => {
                                                const selectedDiagnosis = values.DiagnosisSecondList.find((d) => d.Code === e.target.value);
                                                handleOnChangeDifalultDx(selectedDiagnosis, 'SecondDiagnosis', 'DiagnosisThirdList');
                                            }}
                                            error={Boolean(errors.SecondDiagnosis || errors.SecondDiagnosis?.Code) && Boolean(submitCount)}
                                        >
                                            <MenuItem value={0}>Select Diagnosis</MenuItem>
                                            {values.DiagnosisSecondList.map((category) => (
                                                <MenuItem
                                                    key={`menu_item_${category.Code}`}
                                                    value={category.Code}
                                                >{`${category.Code} ${category.Name}`}</MenuItem>
                                            ))}
                                        </Select>
                                        {Boolean(errors.SecondDiagnosis || errors.SecondDiagnosis?.Code) && Boolean(submitCount) ? (
                                            <FormHelperText error>Diagnosis is required.</FormHelperText>
                                        ) : null}
                                    </FormControl>
                                ) : null}
                                {Boolean(values.DiagnosisThirdList?.length) ? (
                                    <FormControl fullWidth error={Boolean(errors.ThirdDiagnosis || errors.ThirdDiagnosis?.Code) && Boolean(submitCount)}>
                                        <Select
                                            fullWidth
                                            id={`default-primary-third-select`}
                                            value={Boolean(values.ThirdDiagnosis?.Code) ? values.ThirdDiagnosis.Code : 0}
                                            onChange={(e) => {
                                                const selectedDiagnosis = values.DiagnosisThirdList.find((d) => d.Code === e.target.value);
                                                handleOnChangeDifalultDx(selectedDiagnosis, 'ThirdDiagnosis', 'DiagnosisFourthList');
                                            }}
                                            error={Boolean(errors.ThirdDiagnosis || errors.ThirdDiagnosis?.Code) && Boolean(submitCount)}
                                        >
                                            <MenuItem value={0}>Select Diagnosis</MenuItem>
                                            {values.DiagnosisThirdList.map((category) => (
                                                <MenuItem
                                                    key={`menu_item_${category.Code}`}
                                                    value={category.Code}
                                                >{`${category.Code} ${category.Name}`}</MenuItem>
                                            ))}
                                        </Select>
                                        {Boolean(errors.ThirdDiagnosis || errors.ThirdDiagnosis?.Code) && Boolean(submitCount) ? (
                                            <FormHelperText error>Diagnosis is required.</FormHelperText>
                                        ) : null}
                                    </FormControl>
                                ) : null}
                                {Boolean(values.DiagnosisFourthList?.length) ? (
                                    <FormControl fullWidth error={Boolean(errors.FourthDiagnosis || errors.FourthDiagnosis?.Code) && Boolean(submitCount)}>
                                        <Select
                                            fullWidth
                                            id={`default-primary-fourth-select`}
                                            value={Boolean(values.FourthDiagnosis?.Code) ? values.FourthDiagnosis.Code : 0}
                                            onChange={(e) =>
                                                handleOnChangeDifalultDx(
                                                    values.DiagnosisFourthList.find((d) => d.Code === e.target.value),
                                                    'FourthDiagnosis'
                                                )
                                            }
                                            error={Boolean(errors.FourthDiagnosis || errors.FourthDiagnosis?.Code) && Boolean(submitCount)}
                                        >
                                            <MenuItem value={0}>Select Diagnosis</MenuItem>
                                            {values.DiagnosisFourthList.map((category) => (
                                                <MenuItem
                                                    key={`menu_item_${category.Code}`}
                                                    value={category.Code}
                                                >{`${category.Code} ${category.Name}`}</MenuItem>
                                            ))}
                                        </Select>
                                        {Boolean(errors.FourthDiagnosis || errors.FourthDiagnosis?.Code) && Boolean(submitCount) ? (
                                            <FormHelperText error>Diagnosis is required.</FormHelperText>
                                        ) : null}
                                    </FormControl>
                                ) : null}
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    className="text-transform-none"
                                    disabled={!Boolean(values.Diagnosis) || isSavingDefaultDiagnosis}
                                    endIcon={isSavingDefaultDiagnosis ? <CircularProgress size={18} color="inherit" /> : null}
                                >
                                    Save
                                </Button>
                                <Button variant="outlined" onClick={handleOnCloseDefaultDiagnosis} className="text-transform-none">
                                    Cancel
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        );
    };

    const renderWarnings = () => {
        return (
            <Collapse in={showWarnings} timeout="auto" unmountOnExit>
                <List sx={classes.warningList}>
                    {assessmentWarnings.map((warning, index) => (
                        <ListItem key={`warning-item-${index}`}>{warning}</ListItem>
                    ))}
                </List>
            </Collapse>
        );
    };

    const renderSubmissionSummaryModal = () => {
        const actionLabel = pendingSubmissionAction?.isReview ? 'Save for Review' : 'Submit Claim';
        const isSubmitClaim = !pendingSubmissionAction?.isReview;
        const encounterPlace = getEncounterPlace(engagementDraft.MeansOfEngagement);
        const memberName = `${member?.FirstName || ''} ${member?.LastName || ''}`.trim() || memberBasicInfo?.Name || memberId;

        return (
            <Dialog open={showSubmissionSummaryModal} onClose={closeSubmissionSummaryModal} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
                    <Typography variant="h6" fontWeight="bold">
                        {`Review Encounter Details Before ${actionLabel}`}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <Stack>
                        <Alert
                            severity={isSubmitClaim ? 'warning' : 'info'}
                            sx={{ borderRadius: 0, border: 'none', borderBottom: '1px solid', borderColor: isSubmitClaim ? 'warning.light' : 'info.light' }}
                        >
                            {isSubmitClaim
                                ? 'You are about to submit this encounter for claim generation. This action cannot be undone. Please verify all coding information is correct before confirming.'
                                : 'You are about to save this encounter for clinical review. Verify the encounter details are accurate before proceeding.'}
                        </Alert>

                        <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1.5, fontSize: '0.7rem' }}>
                                Encounter Summary
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: 'grey.50' }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontWeight: 600, letterSpacing: 0.8, mb: 0.5 }}
                                        >
                                            MEMBER
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {memberName}
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: 'grey.50' }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontWeight: 600, letterSpacing: 0.8, mb: 0.5 }}
                                        >
                                            MEANS / PLACE OF ENCOUNTER
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">{`${
                                            MeansOfEngagementEnum[engagementDraft.MeansOfEngagement] || '-'
                                        } / ${encounterPlace?.Name || '-'}`}</Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider />

                        <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1.5, fontSize: '0.7rem' }}>
                                Encounter Details
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                                            <TableCell sx={{ fontWeight: 'bold', width: 40, color: 'text.secondary' }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>CPT Code</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Primary Diagnosis</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(encounterList.Encounters || []).map((encounter, index) => {
                                            const categoryName = cptCodeCategories.find((c) => c.Id === encounter.CategoryGroupId)?.GroupName || '-';
                                            const cptCodeDetail = prepareCPTCodes(encounter.CategoryGroupId).find((c) => c.Id === encounter.CptCodeId);
                                            const cptCode = encounter.CptCode || cptCodeDetail?.Code || '-';
                                            const cptDescription = cptCodeDetail?.Description;
                                            const primaryDx = encounter.PrimaryDiagnosis?.DiagnosisPointer?.Code
                                                ? `${encounter.PrimaryDiagnosis.DiagnosisPointer.Code} – ${
                                                      encounter.PrimaryDiagnosis.DiagnosisPointer.Name || ''
                                                  }`.trim()
                                                : encounter.PrimaryDiagnosis?.Diagnosis || '-';

                                            return (
                                                <TableRow
                                                    key={`submission-summary-${encounter.Id || encounter.tempId || index}`}
                                                    sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' }, '&:last-child td': { border: 0 } }}
                                                >
                                                    <TableCell sx={{ color: 'text.disabled', fontWeight: 'bold' }}>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {categoryName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {cptCode}
                                                        </Typography>
                                                        {cptDescription && (
                                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                                                {cptDescription}
                                                            </Typography>
                                                        )}
                                                    </TableCell>

                                                    <TableCell sx={{ maxWidth: 240 }}>
                                                        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {primaryDx}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button variant="outlined" onClick={closeSubmissionSummaryModal} size="large">
                        Cancel
                    </Button>
                    <Button variant="contained" color={isSubmitClaim ? 'primary' : 'success'} onClick={confirmSubmissionSummary} size="large">
                        {`Confirm ${actionLabel}`}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const isPhq9Restricted = (encounter) => {
        const code = prepareCPTCodes(encounter.CategoryGroupId).find((c) => c.Id === encounter.CptCodeId)?.Code;
        return ['G8510', 'G8511', 'G8431'].includes(code) && !authData.Permissions.includes('PHQ9 Score Greater Than 14 Alert');
    };

    return (
        <Grid container direction="column" spacing={2} className="content-panel">
            <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 2 }}>
                <Typography variant="h6">Procedure Sign-Off</Typography>
                <Tooltip title="Procedure Sign-Off Help Document">
                    <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                </Tooltip>
            </Stack>
            <Grid className="content-form">
                {isLoading || isFetching ? (
                    <Box display="flex" justifyContent="center" className="loader-container">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Stack direction="row" justifyContent="space-between" spacing={2} marginBottom={4}>
                            {disableWarnings ? (
                                <div />
                            ) : (
                                <Button variant="text" onClick={() => setShowWarnings(!showWarnings)}>
                                    {showWarnings ? 'Hide' : 'Show'} Warnings
                                </Button>
                            )}
                            <Button variant="outlined" onClick={() => setShowDefaultDiagnosisModal(true)}>
                                Set default primary diagnosis
                            </Button>
                        </Stack>
                        {renderWarnings()}
                        <FormControl style={{ marginBottom: '16px', width: '150px' }}>
                            <InputLabel id="demo-simple-select-label">Gender</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Gender"
                                value={memberBasicInfo.Gender ? genders.find((x) => x.label === memberBasicInfo.Gender)?.value : ''}
                                onChange={(e: any) => {
                                    dispatch(setMemberBasicInfo({ ...memberBasicInfo, Gender: genders.find((x) => x.value === e.target.value).label }));
                                    dispatch(saveGender(memberBasicInfo.Id, e.target.value));
                                }}
                            >
                                {genders.map(function (option) {
                                    return (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                        <TableContainer component={Paper} sx={classes.procedureOffTable}>
                            <Table aria-label="simple table" style={{ tableLayout: 'fixed' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width="25%">CPT Category</TableCell>
                                        <TableCell width="25%">Primary CPT</TableCell>
                                        <TableCell width="15%"></TableCell>
                                        <TableCell width="25%">Modifiers</TableCell>
                                        <TableCell width="10%"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {encounterList.Encounters?.map((encounter, index) => (
                                        <React.Fragment key={`encounter_list_${index}_${encounter.Id || encounter.tempId}`}>
                                            <TableRow>
                                                <TableCell component="th" scope="row">
                                                    <Select
                                                        fullWidth
                                                        id={`admission-type-select-${index}`}
                                                        value={encounter.CategoryGroupId}
                                                        size="small"
                                                        className="hide-textinput-label"
                                                        onChange={(e) => handleOnChangeCpt(e.target.value, 'CategoryGroupId', index)}
                                                        disabled={isPhq9Restricted(encounter)}
                                                    >
                                                        {cptCodeCategories.map((category) => (
                                                            <MenuItem key={`menu_item_${category.Id}`} value={category.Id}>
                                                                {category.GroupName}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    <Autocomplete
                                                        id={`cpt-codes-auto-complete-${index}`}
                                                        isOptionEqualToValue={(option, value) => option.Id === value.Id}
                                                        getOptionLabel={(option) => (option as any).Name}
                                                        inputValue={
                                                            prepareCPTCodes(encounter.CategoryGroupId).find((c) => c.Id === encounter.CptCodeId)?.Name || ''
                                                        }
                                                        value={
                                                            (prepareCPTCodes(encounter.CategoryGroupId).find((c) => c.Id === encounter.CptCodeId) ||
                                                                null) as any
                                                        }
                                                        disabled={isPhq9Restricted(encounter)}
                                                        options={filteredCPTCodes(encounter.CategoryGroupId)}
                                                        getOptionDisabled={(option) =>
                                                            encounterList.Encounters.some((e) => e.CptCodeId === option.Id && e.CptCodeId !== encounter.Id)
                                                        }
                                                        size="small"
                                                        freeSolo
                                                        onChange={(e, value) =>
                                                            handleOnChangeCptCode((value as any)?.Id, 'CptCodeId', index, encounter.CategoryGroupId)
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                className="hide-textinput-label"
                                                                variant="outlined"
                                                                error={showCptErrorMsg && !Boolean(encounter.CptCode)}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    endAdornment: <React.Fragment>{params.InputProps.endAdornment}</React.Fragment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    {encounter.CptCodeId &&
                                                    prepareCPTCodes(encounter.CategoryGroupId).find((c) => c.Id === encounter.CptCodeId) ? (
                                                        <Select
                                                            disabled={
                                                                !prepareCPTCodes(encounter.CategoryGroupId).find((c) => c.Id === encounter.CptCodeId)
                                                                    ?.HasMultiUnit
                                                            }
                                                            fullWidth
                                                            id={`units-type-select-${index}`}
                                                            value={encounter.Units}
                                                            size="small"
                                                            className="hide-textinput-label"
                                                            defaultValue={1}
                                                            onChange={(e) => handleOnChangeCpt(e.target.value, 'Units', index)}
                                                        >
                                                            <MenuItem value={0} disabled>
                                                                Select Units
                                                            </MenuItem>
                                                            {prepareCPTUnits().map((unit, index) => (
                                                                <MenuItem key={`menu_item_${unit.Id}`} value={unit.Units}>
                                                                    {unit.Units * unit.UnitValue} {unit.UnitLabel}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    ) : null}
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <TextField
                                                            size="small"
                                                            className="hide-textinput-label"
                                                            inputProps={{ maxLength: 2, style: { width: '40px' } }}
                                                            value={encounter.Modifier || ''}
                                                            onChange={(e) => handleOnChangeCpt(e.target.value, 'Modifier', index)}
                                                        />
                                                        {Boolean(encounter.Modifier) && (
                                                            <TextField
                                                                size="small"
                                                                className="hide-textinput-label"
                                                                inputProps={{ maxLength: 2, style: { width: '40px' } }}
                                                                value={encounter.Modifier2 || ''}
                                                                onChange={(e) => handleOnChangeCpt(e.target.value, 'Modifier2', index)}
                                                            />
                                                        )}
                                                        {Boolean(encounter.Modifier2) && (
                                                            <TextField
                                                                size="small"
                                                                className="hide-textinput-label"
                                                                inputProps={{ maxLength: 2, style: { width: '40px' } }}
                                                                value={encounter.Modifier3 || ''}
                                                                onChange={(e) => handleOnChangeCpt(e.target.value, 'Modifier3', index)}
                                                            />
                                                        )}
                                                        {Boolean(encounter.Modifier3) && (
                                                            <TextField
                                                                size="small"
                                                                className="hide-textinput-label"
                                                                inputProps={{ maxLength: 2, style: { width: '40px' } }}
                                                                value={encounter.Modifier4 || ''}
                                                                onChange={(e) => handleOnChangeCpt(e.target.value, 'Modifier4', index)}
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell component="th" align="right">
                                                    <IconButton
                                                        aria-label="delete"
                                                        style={{
                                                            color: isPhq9Restricted(encounter)
                                                                ? 'gray' // color when disabled
                                                                : 'red', // normal color
                                                            cursor: isPhq9Restricted(encounter) ? 'not-allowed' : 'pointer',
                                                        }}
                                                        onClick={() => handleOnClickRemove(index)}
                                                        disabled={isPhq9Restricted(encounter)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={5} className={encounterList.Encounters.length !== index + 1 ? 'bottom-border' : ''}>
                                                    <Box sx={{ pt: 0 }}>
                                                        <Stack direction="row" alignItems="center">
                                                            <Typography variant="h6" component="label">
                                                                Diagnosis
                                                            </Typography>
                                                            <IconButton
                                                                disabled={encounter.DiagnosisList?.length > 3}
                                                                aria-label="delete"
                                                                color="primary"
                                                                onClick={() => handleOnClickAddDiagnosis(index)}
                                                            >
                                                                <AddIcon />
                                                            </IconButton>
                                                        </Stack>
                                                        <Table size="small" style={{ width: '100%', tableLayout: 'fixed' }}>
                                                            <TableBody>
                                                                {(encounter.DiagnosisList || []).map((dx, dxIndex) => {
                                                                    const canShowDefault =
                                                                        dxIndex === 0 && Boolean(memberDefaultDiagnosis?.DefaultDiagnosis?.Diagnosis);
                                                                    const paddingClassName = canShowDefault ? 'pb-50' : '';
                                                                    const defaultDiagnosis = paddingClassName
                                                                        ? memberDefaultDiagnosis?.DefaultDiagnosis?.Diagnosis.split(' ')[0]
                                                                        : '';

                                                                    return (
                                                                        <TableRow
                                                                            key={`diagnosis-row-${index}-${dxIndex}`}
                                                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                                        >
                                                                            <TableCell width="2.5%" className={paddingClassName}>
                                                                                {dxIndex + 1}
                                                                            </TableCell>
                                                                            <TableCell sx={{ pl: 0 }}>
                                                                                <Autocomplete
                                                                                    id={`primary-auto-select-${index}-${dxIndex}`}
                                                                                    fullWidth
                                                                                    isOptionEqualToValue={(option, value) => option.Name === value.Name}
                                                                                    getOptionLabel={(option: any) =>
                                                                                        typeof option === 'object'
                                                                                            ? `${option.Code} ${option.Name}`
                                                                                            : option || ''
                                                                                    }
                                                                                    value={dx.Diagnosis as any}
                                                                                    options={prepareDiagnosis(index)}
                                                                                    loading={
                                                                                        isFetchingDisgnosys &&
                                                                                        focusInputName === `primary-auto-complete-${index}-${dxIndex}`
                                                                                    }
                                                                                    freeSolo
                                                                                    size="small"
                                                                                    renderOption={(props, option) =>
                                                                                        option.Id === diagnosisFromClaimsId ? (
                                                                                            <ListItem
                                                                                                {...props}
                                                                                                onClick={null}
                                                                                                sx={classes.selectInputLinkItem}
                                                                                            >
                                                                                                <Link
                                                                                                    onClick={() =>
                                                                                                        setDiagnosisFromClaims({
                                                                                                            Show: true,
                                                                                                            ParentPropName: 'PrimaryDiagnosis',
                                                                                                            PropName: 'Diagnosis',
                                                                                                            Index: index,
                                                                                                            DxIndex: dxIndex,
                                                                                                        })
                                                                                                    }
                                                                                                >
                                                                                                    {option.Code}
                                                                                                </Link>
                                                                                            </ListItem>
                                                                                        ) : (
                                                                                            <ListItem {...props}>{`${option.Code} ${option.Name}`}</ListItem>
                                                                                        )
                                                                                    }
                                                                                    onChange={(e, value: any) => {
                                                                                        handleOnChangePrimaryDx(value, 'Diagnosis', index, dxIndex);
                                                                                        handleOnChangeSelect(value, index, dxIndex, 'DiagnosisFirstList');
                                                                                    }}
                                                                                    PopperComponent={PopperMy}
                                                                                    renderInput={(params) => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            className="hide-textinput-label"
                                                                                            variant="outlined"
                                                                                            onChange={(e) =>
                                                                                                dispatch(
                                                                                                    getICD10DiagnosisByKeyword(
                                                                                                        e.target.value,
                                                                                                        engagementDraft?.AssessedRole?.Id
                                                                                                    )
                                                                                                )
                                                                                            }
                                                                                            onFocus={() =>
                                                                                                setFocusInputName(`primary-auto-complete-${index}-${dxIndex}`)
                                                                                            }
                                                                                            InputProps={{
                                                                                                ...params.InputProps,
                                                                                                endAdornment: (
                                                                                                    <React.Fragment>
                                                                                                        {isFetchingDisgnosys &&
                                                                                                        focusInputName === `primary-auto-complete-${index}` ? (
                                                                                                            <CircularProgress color="inherit" size={20} />
                                                                                                        ) : null}
                                                                                                        {params.InputProps.endAdornment}
                                                                                                    </React.Fragment>
                                                                                                ),
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                />
                                                                                {canShowDefault ? (
                                                                                    <Button
                                                                                        sx={{ fontSize: '12px' }}
                                                                                        size="small"
                                                                                        className="mt-10"
                                                                                        onClick={() => handleOnClickSetDefaultPrimary(index)}
                                                                                    >
                                                                                        Set {defaultDiagnosis} as primary diagnosis
                                                                                    </Button>
                                                                                ) : null}
                                                                            </TableCell>
                                                                            <TableCell className={paddingClassName}>
                                                                                {Boolean(dx.DiagnosisFirstList?.length) ? (
                                                                                    <Select
                                                                                        fullWidth
                                                                                        id={`primary-first-type-select-${index}`}
                                                                                        value={Boolean(dx.FirstDiagnosis?.Code) ? dx.FirstDiagnosis.Code : 0}
                                                                                        size="small"
                                                                                        onChange={(e) => {
                                                                                            const selectedDiagnosis = dx.DiagnosisFirstList.find(
                                                                                                (d) => d.Code === e.target.value
                                                                                            );
                                                                                            handleOnChangeDx(
                                                                                                selectedDiagnosis,
                                                                                                'FirstDiagnosis',
                                                                                                index,
                                                                                                dxIndex
                                                                                            );
                                                                                            handleOnChangeSelect(
                                                                                                selectedDiagnosis,
                                                                                                index,
                                                                                                dxIndex,
                                                                                                'DiagnosisSecondList'
                                                                                            );
                                                                                        }}
                                                                                        error={!Boolean(dx.FirstDiagnosis?.Code) && showDxError}
                                                                                    >
                                                                                        <MenuItem value={0}>Select Diagnosis</MenuItem>
                                                                                        {dx.DiagnosisFirstList.map((category) => (
                                                                                            <MenuItem
                                                                                                key={`menu_item_${category.Code}`}
                                                                                                value={category.Code}
                                                                                            >{`${category.Code} ${category.Name}`}</MenuItem>
                                                                                        ))}
                                                                                    </Select>
                                                                                ) : null}
                                                                            </TableCell>
                                                                            <TableCell className={paddingClassName}>
                                                                                {Boolean(dx.DiagnosisSecondList?.length) ? (
                                                                                    <Select
                                                                                        fullWidth
                                                                                        id={`primary-second-select-${index}`}
                                                                                        value={Boolean(dx.SecondDiagnosis?.Code) ? dx.SecondDiagnosis.Code : 0}
                                                                                        size="small"
                                                                                        onChange={(e) => {
                                                                                            const selectedDiagnosis = dx.DiagnosisSecondList.find(
                                                                                                (d) => d.Code === e.target.value
                                                                                            );
                                                                                            handleOnChangeDx(
                                                                                                selectedDiagnosis,
                                                                                                'SecondDiagnosis',
                                                                                                index,
                                                                                                dxIndex
                                                                                            );
                                                                                            handleOnChangeSelect(
                                                                                                selectedDiagnosis,
                                                                                                index,
                                                                                                dxIndex,
                                                                                                'DiagnosisThirdList'
                                                                                            );
                                                                                        }}
                                                                                        error={!Boolean(dx.SecondDiagnosis?.Code) && showDxError}
                                                                                    >
                                                                                        <MenuItem value={0}>Select Diagnosis</MenuItem>
                                                                                        {dx.DiagnosisSecondList.map((category) => (
                                                                                            <MenuItem
                                                                                                key={`menu_item_${category.Code}`}
                                                                                                value={category.Code}
                                                                                            >{`${category.Code} ${category.Name}`}</MenuItem>
                                                                                        ))}
                                                                                    </Select>
                                                                                ) : null}
                                                                            </TableCell>
                                                                            <TableCell className={paddingClassName}>
                                                                                {Boolean(dx.DiagnosisThirdList?.length) ? (
                                                                                    <Select
                                                                                        fullWidth
                                                                                        id={`primary-third-select-${index}`}
                                                                                        value={Boolean(dx.ThirdDiagnosis?.Code) ? dx.ThirdDiagnosis.Code : 0}
                                                                                        size="small"
                                                                                        onChange={(e) => {
                                                                                            const selectedDiagnosis = dx.DiagnosisThirdList.find(
                                                                                                (d) => d.Code === e.target.value
                                                                                            );
                                                                                            handleOnChangeDx(
                                                                                                selectedDiagnosis,
                                                                                                'ThirdDiagnosis',
                                                                                                index,
                                                                                                dxIndex
                                                                                            );
                                                                                            handleOnChangeSelect(
                                                                                                selectedDiagnosis,
                                                                                                index,
                                                                                                dxIndex,
                                                                                                'DiagnosisFourthList'
                                                                                            );
                                                                                        }}
                                                                                        error={!Boolean(dx.ThirdDiagnosis?.Code) && showDxError}
                                                                                    >
                                                                                        <MenuItem value={0}>Select Diagnosis</MenuItem>
                                                                                        {dx.DiagnosisThirdList.map((category) => (
                                                                                            <MenuItem
                                                                                                key={`menu_item_${category.Code}`}
                                                                                                value={category.Code}
                                                                                            >{`${category.Code} ${category.Name}`}</MenuItem>
                                                                                        ))}
                                                                                    </Select>
                                                                                ) : null}
                                                                            </TableCell>
                                                                            <TableCell className={paddingClassName}>
                                                                                {Boolean(dx.DiagnosisFourthList?.length) ? (
                                                                                    <Select
                                                                                        fullWidth
                                                                                        id={`primary-fourth-select-${index}`}
                                                                                        value={Boolean(dx.FourthDiagnosis?.Code) ? dx.FourthDiagnosis.Code : 0}
                                                                                        size="small"
                                                                                        onChange={(e) =>
                                                                                            handleOnChangeDx(
                                                                                                dx.DiagnosisFourthList.find((d) => d.Code === e.target.value),
                                                                                                'FourthDiagnosis',
                                                                                                index,
                                                                                                dxIndex
                                                                                            )
                                                                                        }
                                                                                        error={!Boolean(dx.FourthDiagnosis?.Code) && showDxError}
                                                                                    >
                                                                                        <MenuItem value={0}>Select Diagnosis</MenuItem>
                                                                                        {dx.DiagnosisFourthList.map((category) => (
                                                                                            <MenuItem
                                                                                                key={`menu_item_${category.Code}`}
                                                                                                value={category.Code}
                                                                                            >{`${category.Code} ${category.Name}`}</MenuItem>
                                                                                        ))}
                                                                                    </Select>
                                                                                ) : null}
                                                                            </TableCell>
                                                                            <TableCell width="5%" className={paddingClassName}>
                                                                                <IconButton
                                                                                    aria-label="delete"
                                                                                    style={{ color: 'red' }}
                                                                                    onClick={() => handleOnClickRemoveDiagnosis(index, dxIndex)}
                                                                                >
                                                                                    <DeleteIcon />
                                                                                </IconButton>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            size="small"
                            onClick={handleOnClickAdd}
                            style={{ textTransform: 'none', marginTop: '15px' }}
                        >
                            Add More
                        </Button>
                    </>
                )}
                {renderDiagnosisFromClaimsModal()}
            </Grid>
            <Grid justifyContent="flex-end" className="content-footer">
                <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    sx={classes.actionButton}
                    id="btn-encountered"
                    onClick={handleOnSkipEncounters}
                    disabled={isSaving}
                    endIcon={isSaving && clickedBtnId === 'btn-encountered' ? <CircularProgress size={18} color="inherit" /> : null}
                >
                    {isNotEncounterable ? 'Not to be Encountered (Team Assignment Request)' : 'Not to be Encountered'}
                </Button>
                {(isInHra && !isCoder && !isNursePractitioner) || isNotEncounterable ? null : (
                    <React.Fragment>
                        {isInHra && isCoder ? null : (
                            <Button
                                type="submit"
                                variant="contained"
                                color="success"
                                sx={classes.actionButton}
                                id="btn-review"
                                onClick={(e) => openSubmissionSummaryModal(e, true)}
                                disabled={(notAssessment && memberBasicInfo.Status === MemberStatusEnum.UndeterminedDescription) || isSaving}
                                endIcon={isSaving && clickedBtnId === 'btn-review' ? <CircularProgress size={18} color="inherit" /> : null}
                            >
                                Save for Review
                            </Button>
                        )}
                        {(isInHra && isNursePractitioner) || isInTransitionalCare ? null : (
                            <Button
                                type="submit"
                                variant="contained"
                                sx={classes.actionButton}
                                id="btn-claim"
                                onClick={(e) => openSubmissionSummaryModal(e)}
                                disabled={
                                    disableSubmitToClaim || (notAssessment && memberBasicInfo.Status === MemberStatusEnum.UndeterminedDescription) || isSaving
                                }
                                endIcon={isSaving && clickedBtnId === 'btn-claim' ? <CircularProgress size={18} color="inherit" /> : null}
                            >
                                Submit Claim
                            </Button>
                        )}
                    </React.Fragment>
                )}
            </Grid>
            {renderDefaultPrimaryDiagnosis()}
            {renderSubmissionSummaryModal()}
            <AlertDialog
                open={showDiagnosisAlert}
                onConfirm={() => setShowDiagnosisAlert(false)}
                okText="Ok"
                message={`Reminder! HgbA1C is required every six months with Hedis coding.`}
                hideCancelButton={true}
                bottomButtonCenter={true}
            />
            <AlertDialog
                title="HEDIS Alert"
                open={showHedisAlert}
                onConfirm={() => {
                    setShowHedisAlert(false);
                    handleOnSave(eventValue, isReviewValue, true);
                }}
                onClose={() => setShowHedisAlert(false)}
                okText="Continue"
                hideCancelButton={false}
                bottomButtonCenter={true}
                renderBody={() => (
                    <Stack>
                        <Typography variant="body2" component="label" sx={{ paddingBottom: '15px', color: 'gray' }}>
                            Please add below diagnosis code to meet the HEDIS metrics
                        </Typography>
                        <ul style={{ paddingLeft: '20px', margin: '0' }}>
                            {hedisAlertMessage.map((item, index) => {
                                return (
                                    <Typography key={index} variant="subtitle2" component="label">
                                        <li key={index} style={{ whiteSpace: 'normal', paddingBottom: '10px' }}>
                                            {item}
                                        </li>
                                    </Typography>
                                );
                            })}
                        </ul>
                    </Stack>
                )}
            />
        </Grid>
    );
};

export default ProcedureSignOff;
